import { forwardRef } from "react";

export interface InvoiceItem {
  name: string;
  qty: number;
  price: number;
}

export interface InvoicePrintProps {
  orderId: string;
  items: InvoiceItem[];
  subtotal: number;
  discount: number;
  total: number;
  company: {
    name: string;
    nit: string;
    address?: string;
    phone?: string;
    logoUrl?: string;
  };
  customer?: {
    name?: string;
    phone?: string;
    address?: string;
  } | null;
  module: string;
  date: string;
  hour: string;
  paymentMethod: string | null;
  paidAmount: number | null;
  change: number | null;
  fiscalNumber?: string;
  footerNote?: string;
  printerWidth?: "58" | "80";
  printMode?: "ORIGINAL" | "COPY" | "PRE_INVOICE";
}

export const InvoicePrint = forwardRef<HTMLDivElement, InvoicePrintProps>(
  (
    {
      orderId,
      items,
      subtotal,
      discount,
      total,
      company,
      customer,
      module,
      date,
      hour,
      paymentMethod,
      paidAmount,
      change,
      fiscalNumber,
      footerNote = "¡Gracias por su compra!",
      printerWidth = "80",
      printMode = "ORIGINAL",
    },
    ref
  ) => {
    const maxWidth = printerWidth === "58" ? 220 : 300;

    const formatOrderId = (id: string) => {
      if (!id) return "";
      const last6 = id.slice(-6);
      return `***${last6}`;
    };

    return (
      <div
        ref={ref}
        style={{
          width: maxWidth,
          fontFamily: "monospace",
          fontSize: "12px",
          padding: "8px",
          transform: "scale(1)",
          WebkitPrintColorAdjust: "exact",
          printColorAdjust: "exact",
        }}
      >
        {/* LOGO */}
        {company.logoUrl && (
          <div style={{ textAlign: "center", marginBottom: 10 }}>
            <img
              src={company.logoUrl}
              alt="logo"
              style={{ width: "80px", margin: "0 auto" }}
            />
          </div>
        )}

        {/* TITULO DEL DOCUMENTO */}
        <div style={{ textAlign: "center", marginBottom: 8 }}>
          {printMode === "ORIGINAL" && <strong>RECIBO DE COMPRA</strong>}
          {printMode === "COPY" && <strong>COPIA – ORIGINAL</strong>}
          {printMode === "PRE_INVOICE" && (
            <strong>PREFACTURA – NO ES DOCUMENTO DE VENTA</strong>
          )}
        </div>

        {/* ENCABEZADO */}
        <div style={{ textAlign: "center", marginBottom: 10 }}>
          <strong style={{ fontSize: "14px" }}>{company.name}</strong>
          <div>NIT: {company.nit}</div>
          {company.address && <div>{company.address}</div>}
          {company.phone && <div>Tel: {company.phone}</div>}
        </div>

        {/* INFO FACTURA */}
        <div style={{ marginBottom: 8 }}>
          {printMode === "ORIGINAL" && <strong>#{fiscalNumber}</strong>}
          <br />
          <strong>Orden: {formatOrderId(orderId)}</strong>
          <br />
          Módulo: {module}
          <br />
          Fecha: {date}
          <br />
          Hora: {hour}
        </div>

        {/* INFO DEL CLIENTE (NO en PREFACURA) */}
        {printMode !== "PRE_INVOICE" &&
          customer &&
          (customer.name || customer.phone || customer.address) && (
            <div style={{ marginBottom: 10 }}>
              <strong>Cliente:</strong>
              <br />
              {customer.name && <div>{customer.name}</div>}
              {customer.phone && <div>Tel: {customer.phone}</div>}
              {customer.address && <div>Dir: {customer.address}</div>}
            </div>
          )}

        {/* DETALLE */}
        <div style={{ margin: "8px 0" }}></div>
        <strong>Detalle</strong>

        {items.map((i) => (
          <div key={i.name} style={{ marginTop: 4 }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>
                {i.qty} x {i.name}
              </span>
              <span>${i.price.toLocaleString()}</span>
            </div>
            <div
              style={{
                borderTop: "1px dashed black",
                marginTop: 4,
                width: "100%",
              }}
            />
          </div>
        ))}

        {/* RESUMEN */}
        <div style={{ margin: "10px 0" }}></div>

        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span>Subtotal</span>
          <strong>${subtotal.toLocaleString()}</strong>
        </div>

        {discount > 0 && (
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span>Descuento</span>
            <strong>-${discount.toLocaleString()}</strong>
          </div>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: 4,
            fontSize: "14px",
          }}
        >
          <strong>Total</strong>
          <strong>${total.toLocaleString()}</strong>
        </div>

        {/* PAGO (NO SE MUESTRA EN PREFACTURA) */}
        {printMode !== "PRE_INVOICE" && (
          <>
            <div
              style={{
                margin: "10px 0",
                borderTop: "1px dashed black",
                width: "100%",
              }}
            />

            <strong>Método de pago:</strong>
            <div style={{ marginBottom: 6 }}>
              {paymentMethod === "CASH" && "Efectivo"}
              {paymentMethod === "CARD" && "Datáfono"}
              {paymentMethod === "TRANSFER" && "Transferencia"}
            </div>
          </>
        )}

        <div
          style={{
            margin: "10px 0",
            borderTop: "1px dashed black",
            width: "100%",
          }}
        />

        {/* PIE */}
        <div style={{ textAlign: "center", marginTop: 12 }}>
          {printMode === "ORIGINAL" && (
            <>
              {footerNote && (
                <>
                  {footerNote}
                  <br />
                </>
              )}
              <small>
                Fecha de emisión: {date} {hour}
              </small>
              <br />
              <div style={{ margin: "6px 0" }}>----------- FIN -----------</div>
              <small>BCPOS © {new Date().getFullYear()}</small>
            </>
          )}

          {printMode === "COPY" && (
            <>
              <strong style={{ fontSize: "11px" }}>COPIA – ORIGINAL</strong>
              <br />
              <small>
                Emitida: {date} {hour}
              </small>
              <br />
              <small>BCPOS © {new Date().getFullYear()}</small>
            </>
          )}

          {printMode === "PRE_INVOICE" && (
            <>
              <strong>PREFACTURA</strong>
              <br />
              <small>No es documento de venta</small>
              <br />
              <small>
                Generada: {date} {hour}
              </small>
            </>
          )}
        </div>
      </div>
    );
  }
);
